// Hand a question to a person in HR. Goes to hr@novara.com and HR's queue; HR replies by email.

import { useState } from "react";
import { CATEGORIES, type Category } from "@ask-hr/core/escalations";
import { ApiError, api } from "../api.js";

export function TalkToHr({
  conversationId,
  onClose,
  onSent,
}: {
  conversationId: string | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const [category, setCategory] = useState<Category | "">("");
  const [note, setNote] = useState("");
  const [include, setInclude] = useState(Boolean(conversationId));
  const [state, setState] = useState<"editing" | "sending" | "sent">("editing");
  const [error, setError] = useState("");

  async function send() {
    setState("sending");
    setError("");
    try {
      await api("POST", "/api/escalations", {
        category,
        note,
        ...(conversationId ? { conversationId, includeConversation: include } : {}),
      });
      setState("sent");
      onSent();
    } catch (e) {
      setState("editing");
      setError(
        e instanceof ApiError && e.status === 400 && typeof e.body.detail === "string"
          ? e.body.detail
          : "That didn't send. Try again, or email hr@novara.com.",
      );
    }
  }

  if (state === "sent") {
    return (
      <div className="talk" role="status">
        <p>
          <strong>Sent to HR.</strong> They'll reply to you through the Ask HR portal or by email. You can see its status under <em>Your HR requests</em>.
        </p>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <form
      className="talk"
      aria-label="Talk to HR"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <p className="sub">
        This goes to Novara HR (hr@novara.com) with your name and email, and they'll reply to you directly.
      </p>
      <label className="field">
        What's it about?
        <select required value={category} onChange={(e) => setCategory(e.target.value as Category)}>
          <option value="" disabled>
            Choose one
          </option>
          {(Object.entries(CATEGORIES) as [Category, string][]).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        Anything HR should know? <span className="muted">(optional)</span>
        <textarea rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {conversationId ? (
        <label className="check">
          <input type="checkbox" checked={include} onChange={(e) => setInclude(e.target.checked)} /> Include this conversation
        </label>
      ) : null}
      {error ? (
        <p className="notice" role="alert">
          {error}
        </p>
      ) : null}
      <div className="actions start">
        <button type="submit" className="primary" disabled={state === "sending" || !category}>
          {state === "sending" ? "Sending…" : "Send to HR"}
        </button>
        <button type="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
